/**
 * Driver targets — maps agent tool identifiers to their install paths.
 *
 * Only backends with a full bridge driver (runtime execution) are included.
 * Render-only targets (cursor, roo, github-copilot, gemini, amp) were removed —
 * add them back when a bridge driver is implemented.
 */
import { homedir } from "node:os";
import { join } from "node:path";

/**
 * All supported coding-agent driver target identifiers.
 * Only backends with a full bridge driver (runtime execution) are included.
 * @docLink packages/core/concepts#driver-target
 */
export type DriverTarget = "claude-code" | "omp" | "codex";

/**
 * Filesystem install path configuration for a single driver target.
 * Paths are relative to the project root (`local`) or the home directory (`global`).
 * `~` in global paths is expanded to `$HOME` by `deployedBase`.
 * @docLink packages/core/concepts#agent-target
 */
export interface AgentTarget {
  /** Project-relative install paths for each AI component type. */
  local: Record<AiComponent, string>;
  /** Home-relative install paths for each AI component type (`~` = `$HOME`). */
  global: Record<AiComponent, string>;
}

/**
 * The distinct asset types that can be installed into a driver target directory.
 * Used as keys in `AgentTarget.local` and `AgentTarget.global`.
 * @docLink packages/core/concepts#ai-component
 */
export type AiComponent = "skill" | "agent" | "prompt" | "flow" | "contract";

/**
 * Filesystem install path configuration for every supported driver target.
 * `local` paths are relative to the project root; `global` paths are home-relative (`~` = `$HOME`).
 *
 * Only targets with a full bridge driver are included. Removed targets:
 *   cursor         — no bridge driver
 *   roo            — no bridge driver
 *   github-copilot — no bridge driver
 *   gemini         — no bridge driver
 *   amp            — no bridge driver
 *   windsurf       — no file-based agent support
 *   cline          — no file-based agent support
 *   continue       — no file-based agent support
 * @docLink packages/core/concepts#driver-targets
 */
export const DRIVER_TARGETS: Record<DriverTarget, AgentTarget> = {
  "claude-code": {
    local: {
      skill: ".claude/skills",
      agent: ".claude/agents",
      prompt: ".claude/commands",
      flow: ".skaile/flows",
      contract: ".claude/contracts",
    },
    global: {
      skill: "~/.claude/skills",
      agent: "~/.claude/agents",
      prompt: "~/.claude/commands",
      flow: "~/.skaile/flows",
      contract: "~/.claude/contracts",
    },
  },
  omp: {
    local: {
      skill: ".omp/skills",
      agent: ".omp/agents",
      prompt: ".omp/prompts",
      flow: ".skaile/flows",
      contract: ".omp/contracts",
    },
    global: {
      skill: "~/.omp/skills",
      agent: "~/.omp/agents",
      prompt: "~/.omp/prompts",
      flow: "~/.skaile/flows",
      contract: "~/.omp/contracts",
    },
  },
  codex: {
    local: {
      skill: ".codex/skills",
      agent: ".codex/agents",
      prompt: ".codex/rules",
      flow: ".skaile/flows",
      contract: ".codex/contracts",
    },
    global: {
      skill: "~/.codex/skills",
      agent: "~/.codex/agents",
      prompt: "~/.codex/rules",
      flow: "~/.skaile/flows",
      contract: "~/.codex/contracts",
    },
  },
};

/**
 * Array of all `DriverTarget` values that support file-based asset installation
 * (skills, agents, prompts, flows, contracts).
 * @docLink packages/core/concepts#supported-driver-targets
 */
export const SUPPORTED_DRIVER_TARGETS = Object.keys(DRIVER_TARGETS) as DriverTarget[];

/**
 * Absolute path to a driver target's **global backend root** — the home-relative
 * directory that identifies the backend on disk (e.g. `~/.claude`, `~/.omp`,
 * `~/.codex`). Derived from the first path segment of the target's global skill
 * path. Used by `resolveGlobalBackends` to auto-detect which backends the user
 * runs globally (a backend "exists globally" when this dir is present).
 *
 * @param target - Driver target identifier.
 * @returns Absolute path to the backend's home-relative root dir.
 * @docLink packages/core/concepts#global-backend-root
 */
export function globalBackendRoot(target: DriverTarget): string {
  const skillPath = DRIVER_TARGETS[target].global.skill; // e.g. "~/.claude/skills"
  const firstSegment = skillPath.replace(/^~\//, "").split("/")[0]; // ".claude"
  return join(homedir(), firstSegment);
}
